// qq fcts utiles pour manipuler le dom
import { getAbsolutePath } from './kernel'

const svgns = 'http://www.w3.org/2000/svg'

export const fontFamily = 'Roboto, "Noto Sans Arabic", sans-serif'
const idSvgAux = 'mtgSvgAux'

/**
 * Ajoute une image dans ct
 * @param {HTMLElement} ct
 * @param {string} imgName Le nom de l'image (dans src/images, si y'a pas d'extension on ajoute .png)
 * @param {object} options
 * @param {string} [options.alt]
 * @param {string} [options.width]
 * @param {string} [options.height]
 * @returns {HTMLImageElement}
 */
export function addImg (ct, imgName, { alt = '', width = 22, height = 22 } = {}) {
  const img = ce('img')
  img.setAttribute('alt', alt)
  if (width) img.setAttribute('width', `${width}px`)
  if (height) img.setAttribute('height', `${height}px`)
  ct.appendChild(img)
  // on ajoute l'image avec un import dynamique, mais il faut préciser l'extension dans l'import (vite l'exige)
  let suffix = 'png'
  if (imgName.includes('.')) {
    // on pourrait avoir deux points dans le nom, on ne fait donc pas de split mais une regexp
    const [, f, s] = /(.*)\.([^.]*)$/.exec(imgName)
    if (!['png', 'gif'].includes(s)) throw Error(`Suffixe ${s} non géré (png|gif seulement)`)
    imgName = f
    suffix = s
  }
  const p = suffix === 'gif'
    ? import(`src/images/${imgName}.gif`)
    : import(`src/images/${imgName}.png`)
  p.then(({ default: src }) => {
    // pour que ça fonctionne en CORS il faut une url absolue
    img.setAttribute('src', getAbsolutePath(src))
  })
    .catch(error => console.error(error))
  return img
}

/**
 * Crée un élément de tag name avec les attributs de l'objet att passés en paramètre
 * et le retourne
 * @param {string} name
 * @param {Object} [attrs]
 * @returns {HTMLElement}
 */
export function ce (name, attrs) {
  const element = document.createElement(name)
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      element.setAttribute(key, value)
    }
  }
  return element
}

/**
 * Ajoute un élément dans parent (de tag name avec les attributs att)
 * @param {HTMLElement} parent
 * @param {string} name
 * @param {Object<string, string>} [attrs]
 * @param {string} [textContent]
 * @returns {HTMLElement}
 */
export function ceIn (parent, name, attrs, textContent) {
  if (typeof attrs === 'string') {
    textContent = attrs
    attrs = null
  }
  const element = ce(name, attrs)
  if (textContent) element.appendChild(ctn(textContent))
  parent.appendChild(element)
  return element
}

/**
 * Créer un élément name dans le namespace http://www.w3.org/2000/svg
 * @param {string} name tag à créer
 * @param {TagAttributes} att ses attributs
 * @returns {SVGElement}
 */
export function cens (name, att) {
  const element = document.createElementNS(svgns, name)
  if (att) {
    for (const [key, value] of Object.entries(att)) {
      element.setAttribute(key, value)
    }
  }
  /* Supprimé. Inutile (avait été ajouté version MathJax 3)
  if (name === "svg") {
    element.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    element.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  }
  */
  return element
}

/**
 * Crée un TextNode et le retourne (sans l'ajouter dans le dom)
 * @type {function}
 * @param {string} text
 * @return {Text}
 */
export const ctn = (text) => document.createTextNode(text)
/**
 * Crée un TextNode et l'ajoute dans ct
 * @param {HTMLElement} ct
 * @param {string} text
 */
export function addText (ct, text) {
  if (!ct) return console.error(Error('conteneur manquant'))
  ct.appendChild(ctn(text))
}
/**
 * Vide elt
 * @param {HTMLElement} elt
 */
export function empty (elt) {
  while (elt?.lastChild) elt.removeChild(elt.lastChild)
}

/**
 * Retourne true si l'élément est en display none ou qu'il a un parent en display none (mais ça peut renvoyer false et être en visibility hidden)
 * @param {Element} elt
 * @returns {boolean}
 * /
export function hasDisplayNone (elt) {
  if (!elt) {
    console.error(Error('isDisplayed appelé sans élément'))
    return false
  }
  if (elt?.style?.display === 'none') return true
  while (elt?.parentElement) {
    if (elt.parentElement?.style?.display === 'none') return true
    elt = elt.parentElement
  }
  return false
} /* */

/**
 * Alias de getElementById (qui warn en console si ça n'existe pas,
 * sauf avec lax=true où c'est silencieux)
 * @param {string} id
 * @param {boolean} [lax=false] Passer true pour rester silencieux si #{id} n'existe pas
 * @returns {HTMLElement}
 */
export function ge (id, lax = false) {
  const elt = document.getElementById(id)
  if (!elt && !lax) console.warn(`Aucun élément #${id} dans le dom`)
  return elt
}

/**
 * Retourne le premier parent qui est un HTMLElement (null si on en trouve pas)
 * @param {Element|null} elt
 * @return {HTMLElement|null}
 */
export function getFirstHtmlParent (elt) {
  if (!elt) return null
  let parent = elt.parentElement
  while (parent?.parentElement && !/^\[object HTML.*Element\]/.test(Object.prototype.toString.call(parent))) parent = parent.parentElement
  return parent
}

/**
 * Retourne un id qui n'existe pas dans le DOM (prefix + num)
 * @param {string} prefix
 * @returns {string}
 */
export function getNewId (prefix = 'mtg') {
  let i = 1
  while (ge(`${prefix}${i}`, true)) i++
  return `${prefix}${i}`
}

/**
 * Retourne le svg auxiliaire (le crée s'il n'existait pas), avec cette taille minimale.
 * Ce svg auxiliaire en visibility: hidden est nécessaire pour calculer des tailles de contenus,
 * dans le cas où le svg principal serait en display:none au moment de la construction de la figure
 * (car dans ce cas getBBox() comme getBoundingClientRect() retourneraient 0).
 * Cela peut très bien arriver si l'on met une figure dans un div caché au départ
 * que l'on affiche ensuite (c'est par exemple le cas dans les onglet des pages spip).
 * À priori il faut l'appeler en fournissant une taille la première fois (celle du svg principal)
 * @param {Object} [options]
 * @param {number} [options.width]
 * @param {number} [options.height]
 * @return {SVGElement}
 */
export function getSvgAux ({ width, height } = {}) {
  let svgAux = document.getElementById(idSvgAux)
  if (svgAux === null) {
    if (!width || !height) {
      console.warn('Il faudrait passer une taille au premier appel de getSvgAux')
      // on cherche la taille max de tous les svg mathgraph contenus dans le dom
      const playerSvgs = document.querySelectorAll('.mtgAppLecteur')
      const editorSvgs = document.querySelectorAll('.mtgApp')
      const svgs = Array.from(playerSvgs).concat(Array.from(editorSvgs))
      width ??= 0
      height ??= 0
      for (const svg of svgs) {
        const w = Number(svg.getAttribute('width')) || 0
        const h = Number(svg.getAttribute('height')) || 0
        if (w > width) width = w
        if (h > height) height = h
      }
    }
    // on crée un div en fin de body pour y mettre le svgAux, en absolute et hidden, avec z-index -1 pour ne rien modifier visuellement
    // (taille fixe petite et overflow hidden pour ne jamais provoquer de scroll)
    const divSvgAux = ce('div', {
      style: 'position:absolute; left:0; top:0; pointer-events:none; z-index:-1; visibility:hidden; overflow:hidden; width:100px; height:100px;',
      dir: 'ltr'
    })
    document.body.appendChild(divSvgAux)
    svgAux = cens('svg', {
      id: idSvgAux,
      position: 'absolute',
      top: 0,
      left: 0,
      width,
      height,
      visibility: 'hidden',
      style: `font-family: ${fontFamily};`
    })
    divSvgAux.appendChild(svgAux)
  } else {
    // on vérifie la taille min voulue
    // `|| 0`  pour passer dans le if qui suit en cas d'attributs folkloriques
    const w = Number(svgAux.getAttribute('width')) || 0
    const h = Number(svgAux.getAttribute('height')) || 0
    if (w < width) {
      svgAux.setAttribute('width', width)
    }
    if (h < height) {
      svgAux.setAttribute('height', height)
    }
  }
  return svgAux
}

/**
 * Lance le chargement d'un script et résoud la promesse quand c'est fait (ou la rejette en cas de timeout)
 * @param {string} url
 * @param {object} [opts]
 * @param {string} [opts.type=text/javascript] le type à mettre sur le tag script
 * @param {object} [opts.timeout=30_000] timeout en ms
 * @returns {Promise<void>}
 */
export function loadScript (url, opts) {
  const type = opts?.type ?? 'text/javascript'
  return new Promise((resolve, reject) => {
    const script = ce('script', { type })
    document.body.appendChild(script)
    const timeout = opts?.timeout ?? 30_000
    // on devrait virer le listener si on rejette, mais il sera visiblement jamais appelé,
    // et s'il l'était ça changerait rien, la promesse sera déjà rejetée
    const timerId = setTimeout(() => reject(Error(`Timeout : impossible de charger ${url} (après ${Math.round(timeout / 1000)}s d’attente)`)), timeout)
    script.addEventListener('load', () => {
      clearTimeout(timerId)
      resolve()
    }, { once: true })
    script.src = url
  })
}

/**
 * Affecte les attributs attrs à l'élément elt
 * @param {HTMLElement|SVGElement|string} elt
 * @param {Object} attrs Les propriétés sont les noms des attributs (et les valeurs devraient être des strings)
 */
export function setAttrs (elt, attrs) {
  const e = typeof elt === 'string' ? ge(elt) : elt
  if (!e) return console.error(Error(`Impossible d’affecter des attributs à un élément inexistant ${typeof elt === 'string' ? `(#${elt})` : ''}`))
  for (const [attr, value] of Object.entries(attrs)) {
    try {
      e.setAttribute(attr, value)
    } catch (error) {
      console.error(`Impossible d'affecter l’attribut ${attr} avec ${value} sur`, elt, error)
    }
  }
}

/**
 * Affecte chaque propriété de style à l'élément
 * @param {HTMLElement|SVGElement|string} elt
 * @param {Object} style Les propriétés devraient être des propriété de style existantes pour l'élément et les valeurs des strings
 */
export function setStyle (elt, style) {
  const e = typeof elt === 'string' ? ge(elt) : elt
  if (!e) return console.error(Error(`Impossible d’affecter le style d’un élément inexistant ${typeof elt === 'string' ? `(#${elt})` : ''}`))
  for (const [attr, value] of Object.entries(style)) {
    try {
      e.style[attr] = value
    } catch (error) {
      console.error(`Pb pour affecter ${value} dans style.${attr}`, error)
    }
  }
}

/**
 * Copie text dans le presse-papiers (ne plante pas si ça marche pas, la promesse sera résolue avec false dans ce cas)
 * @param {string} text
 * @return {Promise<boolean>} true si ça a été copié, false sinon
 */
export async function copyToClipBoard (text) {
  try {
    if (navigator.clipboard) {
      // Cf https://developer.mozilla.org/fr/docs/Web/API/Clipboard#disponibilit%C3%A9_du_presse-papiers
      // il faudrait normalement utiliser l'api Permission
      // https://developer.mozilla.org/fr/docs/Web/API/Permissions_API
      // pour y accéder, mais tous les navigateurs ne l'implémentent pas de la même manière
      // https://developer.mozilla.org/fr/docs/Web/API/Clipboard#disponibilit%C3%A9_du_presse-papiers
      await navigator.clipboard.writeText(text)
      return true // si writeText plante c'est parti dans le catch
    }
    return false
  } catch (error) {
    console.error(error)
    return false
  }
}

/**
 * Copie le contenu de elt dans le presse-papiers (ne plante pas si ça marche pas, la promesse sera résolue avec false dans ce cas)
 * @param {HTMLElement} elt
 * @return {Promise<boolean>} true si ça a été copié, false sinon
 */
export async function copyContentToClipBoard (elt) {
  try {
    const text = elt.textContent
    // on tente d'abord la méthode moderne
    if (await copyToClipBoard(text)) return true
    // on tente le deprecated execCommand
    if (typeof document.execCommand === 'function') {
      elt.select()
      return document.execCommand('copy')
    }
    // ça veut pas :-/
    return false
  } catch (error) {
    console.error(error)
    return false
  }
}

/**
 * Impose le style du conteneur du svg, dans le cas MtgApp uniquement,
 * pour un calcul correct des positionnements
 * (input & boites de dialogue de l'éditeur, positionnement de la palette de droite, …)
 * Pour MtgAppLecteur, on se limite à fixSvgStyle
 * @param {Object} opts
 * @param {HTMLElement} opts.container
 * @param {number} opts.height
 * @param {number} opts.width
 * @param {SVGSVGElement} opts.svg le svg qui contient la figure
 */
export function fixSvgContainer ({ container, height, width, svg }) {
  container.style.boxSizing = 'border-box'
  // on lui impose un positionnement relatif, sauf s'il est déjà positionné (absolute|fixed|sticky => tout ce qui n'est pas static)
  // (car on a des éléments qui seront positionnés par rapport à ce conteneur
  // nos boites de dialogue par ex)
  if (getComputedStyle(container).position === 'static') {
    container.style.position = 'relative'
  }
  // Pour que Mathjax ne traite pas automatiquement le contenu
  container.classList.add('text2jax_ignore', 'mtgAppContainer')
  container.style.margin = '0'
  container.style.padding = '0'
  container.style.border = 'none'
  // on lui colle la taille imposée
  container.style.width = width + 'px'
  container.style.height = height + 'px'
  // Depuis le 18/3/2025 on impose la fonte 'Roboto' sur tous les conteneurs de svg mtg32
  // (inutile de charger la police, c'est mtgLoad qui le fait)
  // Noto Sans Arabic sera chargé si la langue est l'arabe, pas un pb de le déclarer ici quand même
  container.style.fontFamily = 'Roboto, "Noto Sans Arabic", sans-serif'
  // Il faut fixer ltr même en arabe, à cause des \text latex,
  // c'est l'appli qui gère les cas rtl en triant parmi les contenus
  container.setAttribute('dir', 'ltr')
  // et on passe au svg
  fixSvgStyle({ height, width, svg })
  // Et on impose également ça pour l'éditeur,
  // il faut le forcer à coller au conteneur (d'où top/left/bottom/right à 0),
  // pour que les coordonnées de positionnement dans le svg
  // (pour le contenu html en foreignObject dans le svg)
  // soient les mêmes que celles dans le conteneur
  // (pour les boites de dialogue de l'éditeur)
  // Attention, on a aussi du foreignObject dans le cas player (bouton +/- des inputs numériques par ex)
  svg.style.inset = '0' // top/left/bottom/right
}

/**
 * Impose le style minimal requis pour le svg d'une figure du player
 * (dans le cas éditeur c'est fixSvgContainer qu'il faut appeler)
 * @param {Object} opts
 * @param {number} opts.height
 * @param {number} opts.width
 * @param {SVGSVGElement} opts.svg
 */
export function fixSvgStyle ({ height, width, svg }) {
  // Pour que Mathjax ne traite pas automatiquement le contenu
  svg.classList.add('text2jax_ignore')
  svg.parentElement.classList.add('mtgFigContainer')
  // pour le positionnement de foreign objects, les boutons +/- par ex
  if (getComputedStyle(svg).position === 'static') {
    svg.style.position = 'relative'
  }
  // on impose aussi ça pour éviter tout pb de calcul de coordonnées
  svg.style.margin = '0'
  svg.style.padding = '0'
  svg.style.border = 'none'
  // la taille
  svg.setAttribute('width', width)
  svg.setAttribute('height', height)
  svg.style.width = `${width}px`
  svg.style.height = `${height}px`
  // On rend tous les éléments du svg réactifs, pas seulement les visibles
  // cf https://developer.mozilla.org/fr/docs/Web/CSS/Reference/Properties/pointer-events
  // Yves : Non il ne faut pas car ça casse tout le fonctionnemeny des icvônes boutons
  // il y a de nombreux boutons avec l'attribut visibility à hidden qui se mettent
  // à réagir aux clics de souris. Donc ligne ci-dessous à proscrire absolument
  // svg.style.pointerEvents = 'all'
  // le player ne l'impose pas sur le parent, on le met sur le svg
  // (inutile de charger la police, c'est mtgLoad qui le fait)
  svg.style.fontFamily = 'Roboto, "Noto Sans Arabic", sans-serif'
  // Il faut fixer ltr même en arabe, à cause des \text latex,
  // c'est l'appli qui gère les cas rtl en triant parmi les contenus
  svg.setAttribute('dir', 'ltr')
  // et on veut privilégier la précision des formes sur la vitesse ou le contraste des bords
  // cf https://developer.mozilla.org/fr/docs/Web/CSS/Reference/Properties/shape-rendering
  svg.setAttribute('shape-rendering', 'geometricPrecision')
}
